import {
    AnimationBaseController,
    Collisions,
    GameRandom,
    IAnimationControllerConfiguration,
    Inject,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../../../../AnimationContents';
import { BussinesTripModel } from '../Models/BussinesTripModel';
import {
    BussinesTripMarkerController,
    IBussinesTripMarkerControllerOptions,
} from './BussinesTripMarkerController';
import { BussinesTripLogic } from '../Logic/BussinesTripLogic';
import { BussinesTripMarkerStateEnum } from '../Models/BussinesTripMarkerModel';

export class BussinesTripController extends AnimationBaseController {
    $model: BussinesTripModel;

    private _answerCounter: number = 0;

    private _width: number = 0;
    private _height: number = 0;

    private _screenWidth: number = 0;
    private _screenHeight: number = 0;
    private _screenScale: number = 1;

    public constructor(
        @Inject private _options,
        @Inject private _gameLogic: BussinesTripLogic,
        @Inject private _random: GameRandom,
        @Inject private _collisions: Collisions
    ) {
        super('BussinesTripController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return {
            animationContent: AnimationContents.BUSSINES_TRIP_JSON,
            libraryName: 'mini_game_bussines_trip_container',
            modelWatch: {
                routeIndex: (value: number) => {
                    this.setRouteIndex(value);
                },
            },
            queue: {
                success: {
                    condition: (clock: number) => clock > 1,
                    action: () => {
                        this.$model.selectedMarkers.forEach(
                            o => (o.state = BussinesTripMarkerStateEnum.none)
                        );

                        if (!this._gameLogic.nextRoute()) {
                            if (this._options.onComplete) {
                                this._options.onComplete();
                            }
                        } else {
                            this.$model.markers.forEach(
                                o => (o.interactive = true)
                            );
                        }
                    },
                },
                failed: {
                    condition: (clock: number) => clock > 1,
                    action: () => {
                        this.$model.selectedMarkers.forEach(
                            o => (o.state = BussinesTripMarkerStateEnum.none)
                        );
                        this.$model.markers.forEach(
                            o => (o.interactive = true)
                        );
                        this._gameLogic.clearSelectedCities();

                        if (this._options.onFail && this._answerCounter++ < 1) {
                            this._options.onFail();
                        }
                    },
                },
            },
        };
    }

    protected onInitialize(): void {
        this.gotoAndStopByQuery('mini_game_bussines_trip', 'Idle');

        this.$model.markersAll.forEach(marker => {
            const name = marker.name;
            const controller = this.addChildController(
                {
                    instanceName: `mini_game_bussines_trip.${name}`,
                    view: BussinesTripMarkerController,
                    model: marker,
                    options: <IBussinesTripMarkerControllerOptions>{
                        name: name,
                        onSelected: (name: string) => this.selectCity(name),
                    },
                },
                name
            );
            controller.visible = !!this.$model.markers.find(
                o => o.name === name
            );
        });

        this.setRouteIndex(this.$model.routeIndex);

        this._width = this.display.width;
        this._height = this.display.height;
    }

    protected onResize(width: number, height: number, scale: number) {
        this._screenWidth = width;
        this._screenHeight = height;
        this._screenScale = scale;

        this.updateScale();
    }

    protected onLandscape(): void {
        this.updateScale();
    }

    protected onPortrait(): void {
        this.updateScale();
    }

    private updateScale(): void {
        this.display.scale.set(
            Math.min(
                this._screenWidth / this._width / this._screenScale,
                this._screenHeight / this._height / this._screenScale
            )
        );
    }

    private setRouteIndex(value: number): void {
        if (value < this.$model.routesCount) {
            const route = this._gameLogic.getCurrentRoute();
            const cities = route.split('_');

            this.gotoAndStopByQuery('mini_game_bussines_trip', 'Idle');
            this.setTextByQuery(
                `mini_game_bussines_trip.city_a_label`,
                this.$model.markerStrings[cities[0]]
            );
            this.setTextByQuery(
                `mini_game_bussines_trip.city_b_label`,
                this.$model.markerStrings[cities[1]]
            );
        }
    }

    private selectCity(name: string): void {
        const selectedCities = this._gameLogic.getSelectedCities();

        this.$model.selectedMarkers.find(o => o.name === name).state =
            BussinesTripMarkerStateEnum.selected;

        if (selectedCities.length === 2) {
            this.$model.markers.forEach(o => (o.interactive = false));

            if (this._gameLogic.isMatch()) {
                this.setAnimationControlByParentByQuery(
                    'mini_game_bussines_trip',
                    false
                );
                this.gotoAndStopByQuery(
                    'mini_game_bussines_trip',
                    this._gameLogic.getRouteByPattern()
                );

                this.$queue.get('success').addToQueue();
            } else {
                this.$model.selectedMarkers.forEach(
                    o => (o.state = BussinesTripMarkerStateEnum.failed)
                );
                this.$queue.get('failed').addToQueue();
            }
        }
    }
}
